import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import OrderApi from "../Api/OrderApi";
import { formatDate } from "../helper/DateHelper";
import { Modal, Button } from "react-bootstrap";
import { IMAGE_BASE_URL } from "../Config/Index";
import usePermissions from "../hook/usePermissions";

const numberToWords = (num) => {
    if (num === 0) return "Zero";
    const a = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const g = ['', 'Thousand', 'Million', 'Billion', 'Trillion', 'Quadrillion'];

    const makeGrp = (n) => {
        let st = '';
        if (n >= 100) {
            st += a[Math.floor(n / 100)] + ' Hundred ';
            n %= 100;
        }
        if (n >= 20) {
            st += b[Math.floor(n / 10)] + ' ';
            n %= 10;
        }
        if (n > 0) {
            st += a[n] + ' ';
        }
        return st;
    };

    let str = '';
    let i = 0;
    let integerPart = Math.floor(num);
    let decimalPart = Math.round((num - integerPart) * 100);

    while (integerPart > 0) {
        if (integerPart % 1000 !== 0) {
            str = makeGrp(integerPart % 1000) + g[i] + ' ' + str;
        }
        integerPart = Math.floor(integerPart / 1000);
        i++;
    }

    let result = "INR " + str.trim();
    if (decimalPart > 0) {
        result += " and " + makeGrp(decimalPart).trim() + " Paise";
    }

    return result;
};

const CustomerOrderDetailsLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = usePermissions();

    const sourceModule = location.state?.sourceModule || "All Orders";
    const canEdit = hasPermission(sourceModule, "edit");
    const [orderData, setOrderData] = useState(null);
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [newStatus, setNewStatus] = useState("");
    const [newPaymentStatus, setNewPaymentStatus] = useState("");
    const [cancelReason, setCancelReason] = useState("");
    const [trackingId, setTrackingId] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
    const billRef = useRef();

    const handleVerifyPaymentStatus = async (orderId) => {
        setIsVerifyingPayment(true);
        try {
            const res = await OrderApi.verifyPaymentStatus(orderId);
            if (res && res.status) {
                fetchDetails();
            }
        } catch (err) {
            console.error("Error verifying payment status:", err);
        } finally {
            setIsVerifyingPayment(false);
        }
    };

    const [orderIds, setOrderIds] = useState([]);

    useEffect(() => {
        const stored = sessionStorage.getItem("orderIdsList");
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setOrderIds(parsed);
                    return;
                }
            } catch (e) {
                console.error("Error parsing stored order IDs:", e);
            }
        }

        const fetchOrderIdsList = async () => {
            try {
                const res = await OrderApi.getOrderList(0, 1000, "", "", "", "desc");
                if (res.status && res.response && res.response.data && res.response.data.data) {
                    const ids = res.response.data.data.map(o => o.id || o._id);
                    setOrderIds(ids);
                    sessionStorage.setItem("orderIdsList", JSON.stringify(ids));
                }
            } catch (error) {
                console.error("Error fetching order list for navigation:", error);
            }
        };
        fetchOrderIdsList();
    }, [id]);

    const currentIndex = orderIds.indexOf(id);
    const prevOrderId = currentIndex > 0 ? orderIds[currentIndex - 1] : null;
    const nextOrderId = currentIndex !== -1 && currentIndex < orderIds.length - 1 ? orderIds[currentIndex + 1] : null;

    const handlePrevOrder = () => {
        if (prevOrderId) {
            navigate(`/customer-orders/view/${prevOrderId}`, { state: location.state });
        }
    };

    const handleNextOrder = () => {
        if (nextOrderId) {
            navigate(`/customer-orders/view/${nextOrderId}`, { state: location.state });
        }
    };

    const fetchDetails = async () => {
        setLoading(true);
        setTrackingId("");
        setCancelReason("");
        try {
            const res = await OrderApi.getOrderDetails(id);
            if (res.status && res.response && res.response.data) {
                const order = res.response.data.order || {};
                setOrderData(order);
                setCustomerData(res.response.data.customer || null);
                setNewStatus(order.orderStatus || "");
                setNewPaymentStatus(order.paymentStatus || "Failed");
                setCancelReason(order.cancelReason || "");
                setTrackingId(order.trackingId || "");
            }
        } catch (error) {
            console.error("Error fetching order details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [id]);

    const handleUpdateStatus = async () => {
        if (!newStatus) return;

        if (newStatus === "Cancelled" && !showCancelModal && !orderData.cancelReason) {
            setShowCancelModal(true);
            return;
        }

        setUpdating(true);
        try {
            const res = await OrderApi.updateOrderStatus(id, newStatus, newPaymentStatus, cancelReason, trackingId);
            if (res.status) {
                fetchDetails();
                setShowCancelModal(false);
            }
        } finally {
            setUpdating(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    if (loading) return (
        <div className="d-flex justify-content-center align-items-center py-100">
            <div className="spinner-border text-primary-600" role="status"></div>
        </div>
    );

    if (!orderData) return <div className="text-center py-100">Order not found.</div>;

    const statusSteps = ["Unfulfilled", "Dispatched", "Delivered"];
    const currentStatusIndex = statusSteps.indexOf(orderData.orderStatus === "Unfullied" ? "Unfulfilled" : orderData.orderStatus);
    const progressWidth = (currentStatusIndex / (statusSteps.length - 1)) * 100;

    const cgst = (orderData.taxAmount || 0) / 2;
    const sgst = (orderData.taxAmount || 0) / 2;
    const subtotal = orderData.totalAmount || (orderData.grandTotal - (orderData.taxAmount || 0) - (orderData.shippingCharge || 0) + (orderData.overallDiscount || 0));

    return (
        <div className="order-details-root pb-40">
            <style>{`
                .order-stepper {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    position: relative;
                    padding: 20px 0 40px;
                    max-width: 900px;
                    margin: 0 auto;
                }
                .stepper-bg {
                    position: absolute;
                    top: 42px;
                    left: 0;
                    right: 0;
                    height: 6px;
                    background: #f1f5f9;
                    z-index: 1;
                    border-radius: 10px;
                }
                .stepper-progress {
                    position: absolute;
                    top: 42px;
                    left: 0;
                    height: 6px;
                    background: #334155;
                    z-index: 2;
                    transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
                    border-radius: 10px;
                }
                .step-item {
                    position: relative;
                    z-index: 3;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    width: 120px;
                }
                .step-circle {
                    width: 50px;
                    height: 50px;
                    border-radius: 50%;
                    background: #fff;
                    border: 4px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
                }
                .step-item.active .step-circle {
                    border-color: #334155;
                    background: #334155;
                    color: #fff;
                    transform: scale(1.1);
                }
                .step-item.completed .step-circle {
                    border-color: #334155;
                    background: #334155;
                    color: #fff;
                }
                .step-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #64748b;
                    transition: color 0.3s;
                }
                .step-item.active .step-label, .step-item.completed .step-label {
                    color: #1e293b;
                }

                /* INVOICE CSS FROM WEBSITE */
                .invoice-container {
                    padding: 0;
                    max-width: 100%;
                    margin: 0;
                }
                .invoice-header-actions {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 2rem;
                }
                .print-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: #36533f;
                    color: #fff;
                    border: none;
                    padding: 0.75rem 1.25rem;
                    border-radius: 8px;
                    font-weight: 700;
                    cursor: pointer;
                }
                .invoice-paper {
                    background: #fff;
                    border: 1px solid #eee;
                    padding: 0;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
                    font-family: 'Inter', sans-serif;
                    color: #333;
                }
                .tax-invoice-label {
                    background: #fdfaf9;
                    text-align: center;
                    padding: 0.5rem;
                    font-weight: 700;
                    font-size: 12px;
                    letter-spacing: 0.1em;
                    border-bottom: 1px solid #eee;
                }
                .invoice-header-grid {
                    display: grid;
                    grid-template-columns: 1.2fr 1fr 1fr;
                    border: 1px solid #ccc;
                }
                .header-full-row {
                    grid-column: 1 / -1;
                    padding: 0.5rem;
                    text-align: center;
                    font-size: 14px;
                    font-weight: 800;
                    color: #333;
                    border-bottom: 1px solid #ccc;
                    background: #fff;
                    text-transform: uppercase;
                }
                .branding-cell {
                    grid-row: span 3;
                    padding: 1.5rem;
                    border-right: 1px solid #ccc;
                    text-align: left;
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                }
                .invoice-logo-box {
                    width: 100px;
                    height: 100px;
                    background: #36533f;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                }
                .invoice-logo-box img {
                    width: 80%;
                    height: auto;
                }
                .company-name {
                    font-size: 28px;
                    font-weight: 900;
                    color: #333;
                    margin-bottom: 4px;
                }
                .company-gst {
                    font-size: 14px;
                    font-weight: 800;
                    margin-bottom: 1rem;
                }
                .company-meta {
                    font-size: 12px;
                    color: #555;
                    line-height: 1.6;
                }

                .grid-cell {
                    padding: 1rem;
                    border-bottom: 1px solid #ccc;
                    border-right: 1px solid #ccc;
                }
                .grid-cell:nth-child(3n) {
                    border-right: none;
                }
                .grid-cell.no-border-bottom {
                    border-bottom: none;
                }

                .cell-label {
                    font-size: 12px;
                    font-weight: 800;
                    color: #333;
                    margin-bottom: 4px;
                    display: block;
                }
                .cell-content {
                    font-size: 13px;
                    color: #444;
                }
                .addr-title {
                    font-size: 13px;
                    font-weight: 800;
                    color: #333;
                    margin-bottom: 6px;
                }
                .addr-text {
                    font-size: 12px;
                    line-height: 1.5;
                    color: #555;
                }
                .status-val { font-weight: 800; color: #36533f; }
                .status-val.Failed { color: #c62828; }
                .additional-meta-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    padding: 1rem 2rem;
                    border-bottom: 1px solid #eee;
                    background: #fff;
                }
                .meta-item {
                    display: flex;
                    gap: 1rem;
                }
                .table-resp-wrap {
                    overflow-x: auto;
                }
                .items-table {
                    width: 100%;
                    border-collapse: collapse;
                    min-width: 600px;
                }
                .items-table th {
                    background: #f8f9fa !important;
                    color: #333 !important;
                    border: 1px solid #ddd;
                    padding: 0.75rem;
                    font-size: 12px;
                    font-weight: 800;
                    text-align: center;
                    text-transform: uppercase;
                }
                .items-table td {
                    padding: 0.75rem;
                    border: 1px solid #ddd;
                    vertical-align: middle;
                    font-size: 13px;
                    background-color: #fff !important;
                }
                .items-table tr {
                    background-color: #fff !important;
                }
                .items-table tr:last-child td {
                    border-bottom: 1px solid #ccc;
                }
                .product-thumbnail {
                    width: 50px;
                    height: 50px;
                    object-fit: cover;
                    border-radius: 4px;
                    border: 1px solid #eee;
                }
                .item-name {
                    font-weight: 700;
                    color: #333;
                }
                .summary-row td {
                    padding: 0.5rem 0.75rem !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    color: #666 !important;
                    border: 1px solid #ddd !important;
                }
                .summary-row.border-top td {
                    padding-top: 1rem !important;
                }
                .grand-total-row td {
                    font-size: 16px !important;
                    font-weight: 800 !important;
                    color: #333 !important;
                    background-color: #f9f9f9 !important;
                }
                .amount-summary-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 2rem;
                    background: #fff;
                    border-top: 1px solid #ddd;
                    border-bottom: 1px solid #ddd;
                }
                .amount-summary-row .words-box {
                    font-size: 12px;
                    color: #666;
                }
                .words-val {
                    font-weight: 700;
                    color: #333;
                    text-transform: capitalize;
                }
                .amount-summary-row .pay-box {
                    font-size: 15px;
                    font-weight: 700;
                    color: #333;
                }
                .amt-val {
                    font-size: 20px;
                    color: #36533f;
                    margin-left: 0.5rem;
                }
                .invoice-footer {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    border-bottom: 1px solid #eee;
                }
                .footer-left {
                    padding: 1rem 2rem;
                    border-right: 1px solid #eee;
                }
                .footer-right {
                    padding: 1rem 2rem;
                    display: flex;
                    justify-content: flex-end;
                    align-items: center;
                }
                .notes-title {
                    font-size: 13px;
                    margin: 0 0 0.5rem 0;
                    font-weight: bold;
                }
                .notes-section p {
                    font-size: 12px;
                    color: #444;
                }
                .signatory-box {
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.5rem;
                }
                .signatory-label {
                    font-size: 14px;
                    font-weight: 800;
                    color: #333;
                    margin: 0;
                }
                .signature-area {
                    margin-top: 10px;
                }
                .sig-img {
                    max-width: 150px;
                    height: auto;
                }
                .terms-section {
                    padding: 1rem 2rem;
                    background: #fff;
                }
                .terms-title {
                    font-size: 13px;
                    margin: 0 0 0.5rem 0;
                    font-weight: bold;
                }
                .terms-section ul {
                    margin: 0;
                    padding-left: 1.2rem;
                }
                .terms-section li {
                    font-size: 12px;
                    color: #444;
                    margin-bottom: 0.5rem;
                    line-height: 1.5;
                }
                @media print {
                    aside.sidebar, header.navbar, .footer-section, .no-print {
                        display: none !important;
                    }
                    body {
                        background: #fff !important;
                    }
                    .dashboard-main {
                        margin-left: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                    }
                    .order-details-root {
                        padding: 0 !important;
                        margin: 0 !important;
                        background: #fff !important;
                    }
                    .invoice-container {
                        padding: 0 !important;
                        margin: 0 !important;
                        max-width: none !important;
                        width: 100% !important;
                    }
                    .invoice-paper {
                        border: none !important;
                        box-shadow: none !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .items-table th {
                        background-color: #f1f6f3 !important;
                        -webkit-print-color-adjust: exact;
                    }
                    .tax-invoice-label {
                        background-color: #fdfaf9 !important;
                        -webkit-print-color-adjust: exact;
                    }
                }
                .order-nav-btn {
                    border: none;
                    background: transparent;
                    padding: 6px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 50%;
                    transition: all 0.2s ease-in-out;
                    cursor: pointer;
                }
                .order-nav-btn:hover:not(:disabled) {
                    background-color: #e2e8f0;
                    transform: scale(1.1);
                }
                .order-nav-btn:disabled {
                    opacity: 0.3;
                    cursor: not-allowed;
                }
            `}</style>

            <div className="col-12 no-print">
                <div className="card border-0 shadow-sm radius-12 p-32">
                    <div className="d-flex align-items-center justify-content-between mb-40">
                        <div className="d-flex align-items-center gap-3">
                            <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                                Invoice
                                <button
                                    className="order-nav-btn no-print"
                                    onClick={handlePrevOrder}
                                    disabled={!prevOrderId}
                                    title="Previous Order"
                                >
                                    <Icon icon="lucide:chevron-left" className="text-primary-600" style={{ fontSize: "20px" }} />
                                </button>
                                {orderData.orderId}
                                <button
                                    className="order-nav-btn no-print"
                                    onClick={handleNextOrder}
                                    disabled={!nextOrderId}
                                    title="Next Order"
                                >
                                    <Icon icon="lucide:chevron-right" className="text-primary-600" style={{ fontSize: "20px" }} />
                                </button>
                            </h5>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <button onClick={handlePrint} className="btn btn-primary-600 radius-8 px-24 d-flex align-items-center gap-2" style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}>
                                <Icon icon="solar:printer-bold" />
                                Print
                            </button>
                            <button
                                onClick={() => navigate(-1)}
                                className="btn btn-primary-600 radius-8 px-24 d-flex align-items-center gap-2"
                                style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                            >
                                <Icon icon="lucide:chevron-left" />
                                Back
                            </button>
                        </div>
                    </div>

                    <div className="row g-4 align-items-end p-24 radius-12 border bg-neutral-50">
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light">Tracking ID / URL</label>
                            <input
                                type="text"
                                className="form-control radius-8 h-48-px"
                                placeholder="Enter Tracking ID"
                                value={trackingId}
                                onChange={(e) => setTrackingId(e.target.value)}
                                disabled={!canEdit}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light">Order Status</label>
                            <select
                                className="form-select radius-8 h-48-px"
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                disabled={!canEdit}
                            >
                                {/* <option value="Pending">Pending Orders</option> */}
                                <option value="Unfulfilled">Unfulfilled Orders</option>
                                <option value="Dispatched">Dispatched Orders</option>
                                {/* <option value="Delivered">Delivered Orders</option> */}
                                {/* <option value="Cancelled">Cancelled Orders</option> */}
                                {/* <option value="Return">Return Orders</option> */}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label fw-bold text-secondary-light">Payment Status</label>
                            <select
                                className="form-select radius-8 h-48-px"
                                value={newPaymentStatus}
                                onChange={(e) => setNewPaymentStatus(e.target.value)}
                                disabled={!canEdit}
                            >
                                {/* <option value="Pending">Pending</option> */}
                                <option value="Paid">Paid</option>
                                <option value="Failed">Failed</option>
                                {/* <option value="Partially Paid">Partially Paid</option> */}
                                {/* <option value="Failed">Failed</option> */}
                            </select>
                        </div>
                        <div className="col-md-3">
                            {canEdit && (
                                <button
                                    onClick={handleUpdateStatus}
                                    className="btn btn-primary-600 radius-8 h-48-px w-100 fw-bold"
                                    style={{ backgroundColor: "#364e3d", borderColor: "#364e3d" }}
                                    disabled={updating}
                                >
                                    {updating ? "Updating..." : "Update"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 
            <div className="col-12 no-print mt-4">
                <div className="card border-0 shadow-sm radius-12 p-32">
                    <h6 className="mb-20 fw-bold text-primary-600">Customer Statistics</h6>
                    {customerData ? (
                        <div className="d-flex flex-column gap-4">
                            <div className="d-flex align-items-center gap-3 p-16 radius-12 bg-neutral-50">
                                <div className="w-48-px h-48-px rounded-circle bg-primary-100 d-flex align-items-center justify-content-center text-primary-600">
                                    <Icon icon="solar:user-bold" className="text-2xl" />
                                </div>
                                <div>
                                    <p className="text-xxxxs text-secondary-400 mb-0 fw-bold uppercase">Full Name</p>
                                    <h6 className="text-sm fw-bold text-secondary-light mb-0">{customerData.fullName}</h6>
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-6">
                                    <div className="p-16 radius-12 bg-success-50 h-100">
                                        <p className="text-xxxxs text-success-main mb-4 fw-bold uppercase">Total Orders</p>
                                        <h4 className="mb-0 fw-black text-success-main">{customerData.orderCount}</h4>
                                    </div>
                                </div>
                                <div className="col-6">
                                    <div className="p-16 radius-12 bg-info-50 h-100">
                                        <p className="text-xxxxs text-info-main mb-4 fw-bold uppercase">Order Value</p>
                                        <h4 className="mb-0 fw-black text-info-main">₹{orderData.grandTotal?.toLocaleString()}</h4>
                                    </div>
                                </div>
                            </div>

                            <div className="dashed-divider"></div>

                            <div className="d-flex flex-column gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <Icon icon="solar:phone-bold" className="text-secondary-400 text-sm" />
                                    <span className="text-xs text-secondary-light fw-medium">{customerData.phoneNumber}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Icon icon="solar:letter-bold" className="text-secondary-400 text-sm" />
                                    <span className="text-xs text-secondary-light fw-medium">{customerData.email || 'No Email'}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <Icon icon="solar:calendar-bold" className="text-secondary-400 text-sm" />
                                    <span className="text-xs text-secondary-light fw-medium">Joined {formatDate(customerData.createdAt)}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-20 text-secondary-400 text-sm italic">No profile data available.</div>
                    )}
                </div>
            </div>
            */}

            {/* FULL WIDTH INVOICE */}
            <div className="col-12 mt-4">
                <div className="invoice-container p-0">
                    <div className="invoice-header-actions no-print">
                        <h6 className="mb-0 fw-bold text-primary-600 d-flex align-items-center gap-2">
                            <Icon icon="solar:bill-list-bold-duotone" className="text-2xl" />
                            Tax Invoice
                        </h6>
                        {/* <button onClick={handlePrint} className="print-btn">
                            <Icon icon="solar:printer-bold" className="text-xl" /> Print Invoice
                        </button> */}
                    </div>

                    <div className="invoice-paper" id="invoice-sheet" ref={billRef}>
                        <div className="invoice-header-grid">
                            <div className="header-full-row">Tax Invoice</div>


                            {/* Column 1: Branding (Spans all 3 rows) */}
                            <div className="branding-cell">
                                <div className="invoice-logo-box">
                                    <img src="/logo.jpg" alt="Logo" />
                                </div>
                                <div className="branding-info">
                                    <div className="company-name">Prrayasha Collections</div>
                                    <div className="company-gst">GSTN : 33AWPPJ9059B2ZD</div>
                                    <div className="company-meta">
                                        Provident Cosmo City, DR Abdul Kalam Road,<br />
                                        Pudhupakkam Village, Chengalpet 603103<br />
                                        +91 91590 24967
                                    </div>
                                </div>
                            </div>

                            {/* Row 1: Invoice & Payment */}
                            <div className="grid-cell">
                                <div className="cell-content">
                                    <strong>Invoice # {orderData.orderId || (orderData.id || orderData._id)?.toString().slice(-6).toUpperCase()}</strong>
                                </div>
                                <div className="cell-content" style={{ marginTop: '8px' }}>
                                    Invoice Date: <strong>{orderData.createdAt ? new Date(orderData.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</strong>
                                </div>
                            </div>
                            <div className="grid-cell">
                                <div className="cell-content">
                                    Payment Status: <span className={`status-val ${orderData.paymentStatus?.toLowerCase()}`}>{orderData.paymentStatus}</span>
                                    {orderData.paymentStatus !== "Paid" && (
                                        <button
                                            type="button"
                                            title="Verify Payment Status with PhonePe"
                                            className="btn btn-sm p-1 border-0 bg-transparent text-secondary-light text-hover-primary d-inline-flex align-items-center justify-content-center ms-2"
                                            onClick={() => handleVerifyPaymentStatus(orderData.id || orderData._id)}
                                            disabled={isVerifyingPayment}
                                            style={{ cursor: "pointer", minWidth: "24px", height: "24px" }}
                                        >
                                            <Icon
                                                icon="ion:reload-outline"
                                                className="text-md"
                                                style={{
                                                    animation: isVerifyingPayment ? "spin 1s linear infinite" : "none"
                                                }}
                                            />
                                        </button>
                                    )}
                                </div>
                                <div className="cell-content" style={{ marginTop: '8px' }}>
                                    Payment Method: <strong>{orderData.paymentMethod}</strong>
                                </div>
                                {(() => {
                                    const payObj = orderData?.paymentDetails || orderData?.paymentdetails;
                                    const payData = payObj?.data;
                                    const merchantTxnId = orderData?.transactionId || payData?.merchantTransactionId;
                                    const providerTxnId = payData?.transactionId;

                                    return (
                                        <>
                                            {merchantTxnId && (
                                                <div className="cell-content" style={{ marginTop: '8px' }}>
                                                    Merchant Txn ID: <strong>{merchantTxnId}</strong>
                                                </div>
                                            )}
                                            {providerTxnId && providerTxnId !== merchantTxnId && (
                                                <div className="cell-content" style={{ marginTop: '4px' }}>
                                                    Transaction ID: <strong>{providerTxnId}</strong>
                                                </div>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Row 2: Addresses */}
                            <div className="grid-cell">
                                <div className="addr-title">Billing Address:</div>
                                <div className="addr-text">
                                    Name: {orderData.address?.name}<br />
                                    Phone: {orderData.address?.phone}<br />
                                    Address: {orderData.address?.doorNo}, {orderData.address?.street}<br />
                                    State: {orderData.address?.state}<br />
                                    City: {orderData.address?.city}<br />
                                    Zip: {orderData.address?.pincode}
                                </div>
                            </div>
                            <div className="grid-cell">
                                <div className="addr-title">Shipping Address:</div>
                                <div className="addr-text">
                                    Name: {orderData.address?.name}<br />
                                    Phone: {orderData.address?.phone}<br />
                                    Address: {orderData.address?.doorNo}, {orderData.address?.street}<br />
                                    State: {orderData.address?.state}<br />
                                    City: {orderData.address?.city}<br />
                                    Zip: {orderData.address?.pincode}
                                </div>
                            </div>

                            {/* Row 3: Supply & Due Date */}
                            <div className="grid-cell no-border-bottom">
                                <div className="cell-label">Place of Supply</div>
                                <div className="cell-content"><strong>89 - Chengalpattu</strong></div>
                            </div>
                            <div className="grid-cell no-border-bottom">
                                <div className="cell-label">Due Date:</div>
                                <div className="cell-content"><strong>Immediate on Receipt</strong></div>
                            </div>
                        </div>

                        <div className="table-resp-wrap">
                            <table className="items-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}>#</th>
                                        <th style={{ textAlign: 'left', width: '200px' }}>Item</th>
                                        <th style={{ width: '80px' }}>Photo</th>
                                        <th style={{ width: '100px' }}>Size</th>
                                        <th style={{ width: '100px' }}>HSN/SAC</th>
                                        <th style={{ width: '80px' }}>Quantity</th>
                                        <th style={{ textAlign: 'right', width: '100px' }}>Rate/Item</th>
                                        <th style={{ textAlign: 'right', width: '100px' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orderData.products?.map((item, idx) => (
                                        <tr key={idx}>
                                            <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                            <td style={{ textAlign: 'left' }}>
                                                <div className="item-name">{item.productName}</div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {item.image?.path ? (
                                                    <img
                                                        src={`${IMAGE_BASE_URL}/${item.image.path}`}
                                                        alt={item.productName}
                                                        className="product-thumbnail"
                                                    />
                                                ) : (
                                                    <span style={{ color: '#ccc' }}>No Img</span>
                                                )}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {item.combination?.map(c => c.valueName || c.value).join(', ') || '-'}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>{item.hsnCode || '-'}</td>
                                            <td style={{ textAlign: 'center' }}>{item.qty} Nos</td>
                                            <td style={{ textAlign: 'right' }}>₹{item.price?.toFixed(2)}</td>
                                            <td style={{ textAlign: 'right' }}>₹{item.total?.toFixed(2)}</td>
                                        </tr>

                                    ))}

                                    <tr className="summary-row border-top">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>Total Amount - Inc. all vat/tax</td>
                                        <td style={{ textAlign: 'right' }}>₹{subtotal.toFixed(2)}</td>
                                    </tr>
                                    <tr className="summary-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>CGST 2.5%</td>
                                        <td style={{ textAlign: 'right' }}>₹{cgst.toFixed(2)}</td>
                                    </tr>
                                    <tr className="summary-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>SGST 2.5%</td>
                                        <td style={{ textAlign: 'right' }}>₹{sgst.toFixed(2)}</td>
                                    </tr>
                                    <tr className="summary-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>Shipping Amount</td>
                                        <td style={{ textAlign: 'right' }}>₹{orderData.shippingCharge?.toFixed(2) || '0.00'}</td>
                                    </tr>
                                    {/* {orderData.overallDiscount > 0 && ( */}
                                    <tr className="summary-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>Discount Amount</td>
                                        <td style={{ textAlign: 'right' }}>-₹{orderData.overallDiscount?.toFixed(2) || '0.00'}</td>
                                    </tr>
                                    {/* )} */}
                                    {/* {orderData.shippingDiscount > 0 && (
                                        <tr className="summary-row">
                                            <td colSpan="7" style={{ textAlign: 'right' }}>Shipping Discount</td>
                                            <td style={{ textAlign: 'right' }}>-₹{orderData.shippingDiscount?.toFixed(2)}</td>
                                        </tr>
                                    )} */}
                                    {/* {orderData.couponDiscount > 0 && ( */}
                                    <tr className="summary-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>Coupon Applied</td>
                                        <td style={{ textAlign: 'right' }}>-₹{orderData.couponDiscount?.toFixed(2) || '0.00'}</td>
                                    </tr>
                                    {/* )} */}
                                    <tr className="summary-row grand-total-row">
                                        <td colSpan="7" style={{ textAlign: 'right' }}>Total</td>
                                        <td style={{ textAlign: 'right' }}>₹{orderData.grandTotal?.toFixed(2)}</td>
                                    </tr>

                                </tbody>
                            </table>
                        </div>

                        <div className="amount-summary-row">
                            <div className="words-box">
                                <span>Amount Chargeable (in words): </span>
                                <span className="words-val"> {numberToWords(orderData.grandTotal)}</span>
                            </div>
                            <div className="pay-box">
                                <span>Amount Payable: </span>
                                <span className="amt-val">₹{orderData.grandTotal?.toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="invoice-footer">
                            <div className="footer-left">
                                <div className="notes-section">
                                    <div className="notes-title">Notes:</div>
                                    <p>Thank you for the Shopping</p>
                                </div>
                            </div>
                            <div className="footer-right">
                                <div className="signatory-box">
                                    <div className="signatory-label">For Prrayasha Collection</div>
                                    <div className="signature-area">
                                        <img src="/signature.png" alt="Signature" className="sig-img" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="terms-section">
                            <div className="terms-title">Terms and Conditions:</div>
                            <ul>
                                <li>Products once sold cannot be returned or exchanged unless damage issue.</li>
                                <li>To return or exchange for damage reasons, parcel opening video without any edits and cuts is must and has to shared with us through email (prrayashacollections@gmail.com) or whatsapp (91590 24967) with your order details.</li>
                                <li>No return or exchange for size issues.</li>
                                <li>For any queries or disputes related to products or payments: Email prrayashacollections@gmail.com (response within 2 working days).</li>
                                <li>If no response, contact WhatsApp 91590 24967 (response within 2 working days).</li>
                                <li>If unresolved thereafter, you may proceed with legal action as per applicable laws.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-lg fw-bold">Cancel Order</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-24">
                    <p className="text-sm text-secondary-light mb-12">Please provide a reason for cancelling this order.</p>
                    <textarea
                        className="form-control radius-8"
                        rows="4"
                        placeholder="Enter reason here..."
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                    ></textarea>
                </Modal.Body>
                <Modal.Footer className="border-0 p-24 pt-0">
                    <Button variant="outline-neutral-600" className="radius-8 px-24" onClick={() => setShowCancelModal(false)}>
                        Close
                    </Button>
                    <Button
                        variant="danger"
                        className="radius-8 px-24 fw-bold"
                        onClick={handleUpdateStatus}
                        disabled={updating || !cancelReason.trim()}
                    >
                        Confirm Cancellation
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CustomerOrderDetailsLayer;
